import React from "react";
import { TextField, SxProps, Theme, Box, PopperProps } from "@mui/material";
import { DatePicker as MuiDatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { COMMON_MESSAGES } from "../../../shared/constants/messages";

interface CustomDatePickerProps extends Omit<DatePickerProps<Date, Date>, "renderInput" | "onChange"> {
    sx?: SxProps<Theme>;
    fullWidth?: boolean;
    onChange?: (value: Date | null) => void;
    onValidationError?: (hasError: boolean) => void;
    id?: string;
    /** Harici validasyon için hata durumu (ör: zorunlu alan) */
    externalError?: boolean;
    /** Boş input placeholder. Verilmezse 'gün/ay/yıl' kullanılır. */
    placeholder?: string;
    /** true ise gri varsayılan DatePicker stili uygulanmaz; yalnızca sx kullanılır. */
    suppressDefaultStyles?: boolean;
    /** Takvim açıkken border rengi */
    openBorderColor?: string;
}

type MuiDatePickerOnError = NonNullable<DatePickerProps<Date, Date>["onError"]>;

export interface DatePickerRef {
    validate: () => boolean;
    getCurrentInputValue: () => string;
}

const defaultInputSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
        height: "40px",
        width: "100%",
        borderRadius: "3px",
        backgroundColor: "#F4F5F7",
        cursor: "pointer",
        "& fieldset": { borderColor: "transparent" },
        "&:hover fieldset": { borderColor: "transparent" },
        "&.Mui-focused fieldset": { borderColor: "transparent" },
        "&.Mui-error fieldset": {
            borderColor: "#d32f2f",
        },
        "&.Mui-error:hover fieldset": {
            borderColor: "#d32f2f",
        },
        "&.Mui-error.Mui-focused fieldset": {
            borderColor: "#d32f2f",
            borderWidth: "1px",
        },
        "& .MuiIconButton-root": {
            width: "32px !important",
            height: "32px !important",
            padding: "17px !important",
            borderRadius: "50% !important",
            "&:hover": {
                backgroundColor: "#E5E7EB !important",
            },
        },
        "& .MuiInputBase-input": {
            height: "100%",
            padding: "0 14px",
            cursor: "pointer",
        },
    },
    "& .MuiFormHelperText-root": {
        margin: "4px 14px 0",
        fontSize: "12px",
        fontFamily: "Inter",
    },
};

export const DatePicker = React.forwardRef<DatePickerRef, CustomDatePickerProps>(({
    sx,
    fullWidth = true,
    inputFormat = "dd/MM/yyyy",
    onChange,
    value,
    onValidationError,
    id,
    externalError,
    placeholder = "gün/ay/yıl",
    suppressDefaultStyles = false,
    openBorderColor = "#0065FF",
    ...props
}, ref) => {
    const [error, setError] = React.useState<string | null>(null);
    const [internalValue, setInternalValue] = React.useState<Date | null>(value || null);

    React.useEffect(() => {
        if (value && !isNaN(value.getTime())) {
            const needsUpdate = !internalValue ||
                internalValue.getDate() !== value.getDate() ||
                internalValue.getMonth() !== value.getMonth() ||
                internalValue.getFullYear() !== value.getFullYear();

            if (needsUpdate) {
                setInternalValue(value);
                setError(null);
                if (onValidationError) {
                    onValidationError(false);
                }
            }
        } else if (value === null) {
            setInternalValue(null);
            setError(null);
            if (onValidationError) {
                onValidationError(false);
            }
        }
    }, [value, internalValue, onValidationError]);

    const finalSx = suppressDefaultStyles
        ? sx
        : sx
            ? ([defaultInputSx, sx] as SxProps<Theme>)
            : defaultInputSx;

    const getFieldSx = (isOpen: boolean): SxProps<Theme> => {
        const openSx: SxProps<Theme> = isOpen
            ? {
                "& .MuiOutlinedInput-root:not(.Mui-error) fieldset": {
                    borderColor: `${openBorderColor} !important`,
                    borderWidth: "2px !important",
                },
            }
            : {};

        if (suppressDefaultStyles) {
            return openSx ? ([sx, openSx].filter(Boolean) as SxProps<Theme>) : (sx ?? {});
        }

        return openSx
            ? ([defaultInputSx, sx, openSx].filter(Boolean) as SxProps<Theme>)
            : (finalSx ?? defaultInputSx);
    };

    const getErrorMessage = (errorCode: string | null): string | null => {
        if (!errorCode) return null;

        switch (errorCode) {
            case "invalidDate":
                return COMMON_MESSAGES.INVALID_DATE;
            default:
                return COMMON_MESSAGES.INVALID_DATE_GENERIC;
        }
    };

    const {
        onAccept: propOnAccept,
        onError: propOnError,
        onOpen: propOnOpen,
        onClose: propOnClose,
        open: openProp,
        minDate,
        maxDate,
        shouldDisableYear: userShouldDisableYear,
        shouldDisableMonth: userShouldDisableMonth,
        PopperProps: userPopperProps,
        PaperProps: userPaperProps,
        ...restProps
    } = props as CustomDatePickerProps & { PopperProps?: Partial<PopperProps> };
    const fieldWrapperRef = React.useRef<HTMLDivElement>(null);
    const [internalOpen, setInternalOpen] = React.useState(false);
    const isPickerOpen = openProp ?? internalOpen;

    const userModifiers = userPopperProps?.modifiers;

    const popperProps: Partial<PopperProps> = {
        placement: "bottom-start",
        ...userPopperProps,
        anchorEl: fieldWrapperRef.current,
        modifiers: [
            { name: "offset", options: { offset: [0, 0] } },
            ...(Array.isArray(userModifiers) ? userModifiers : []),
        ],
    };

    const paperProps = {
        ...userPaperProps,
        sx: {
            ...(typeof userPaperProps?.sx === "object" && userPaperProps.sx
                ? userPaperProps.sx
                : {}),
            "& .MuiPickersYear-yearButton.Mui-disabled, & .PrivatePickersYear-yearButton.Mui-disabled": {
                display: "none",
            },
            "& .MuiPickersMonth-root.Mui-disabled, & .PrivatePickersMonth-root.Mui-disabled": {
                display: "none",
            },
        },
    };

    const shouldDisableYear = (date: Date) => {
        if (userShouldDisableYear?.(date)) return true;
        if (minDate && date.getFullYear() < minDate.getFullYear()) return true;
        if (maxDate && date.getFullYear() > maxDate.getFullYear()) return true;
        return false;
    };

    const shouldDisableMonth = (date: Date) => {
        if (userShouldDisableMonth?.(date)) return true;
        if (minDate) {
            const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            if (new Date(date.getFullYear(), date.getMonth(), 1) < minMonth) return true;
        }
        if (maxDate) {
            const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
            if (new Date(date.getFullYear(), date.getMonth(), 1) > maxMonth) return true;
        }
        return false;
    };

    const handleOpen = () => {
        if (openProp === undefined) {
            setInternalOpen(true);
        }
        if (propOnOpen) {
            propOnOpen();
        }
    };

    const handleClose = () => {
        if (openProp === undefined) {
            setInternalOpen(false);
        }
        if (propOnClose) {
            propOnClose();
        }
    };

    const handleAccept = (selectedValue: Date | null) => {
        setError(null);
        if (onValidationError) {
            onValidationError(false);
        }
        if (onChange) {
            onChange(selectedValue);
        }
        if (propOnAccept) {
            propOnAccept(selectedValue);
        }
    };

    const runValidation = React.useCallback((): boolean => {
        if (!internalValue) {
            setError(null);
            if (onValidationError) {
                onValidationError(false);
            }
            return true;
        }

        if (isNaN(internalValue.getTime())) {
            setError(COMMON_MESSAGES.INVALID_DATE);
            if (onValidationError) {
                onValidationError(true);
            }
            return false;
        }

        setError(null);
        if (onValidationError) {
            onValidationError(false);
        }
        return true;
    }, [internalValue, onValidationError]);

    React.useImperativeHandle(ref, () => ({
        validate: () => runValidation(),
        getCurrentInputValue: () => {
            if (!internalValue || isNaN(internalValue.getTime())) return "";
            const day = String(internalValue.getDate()).padStart(2, "0");
            const month = String(internalValue.getMonth() + 1).padStart(2, "0");
            const year = internalValue.getFullYear();
            return `${day}/${month}/${year}`;
        }
    }), [runValidation, internalValue]);

    const handleError: MuiDatePickerOnError = (reason, value) => {
        const errorMessage = getErrorMessage(reason);
        if (errorMessage) {
            setError(errorMessage);
            if (onValidationError) {
                onValidationError(true);
            }
        }
        propOnError?.(reason, value);
    };

    const handleChange = (newValue: Date | null) => {
        setInternalValue(newValue);

        if (newValue && !isNaN(newValue.getTime())) {
            setError(null);
            if (onValidationError) {
                onValidationError(false);
            }
            if (onChange) {
                onChange(newValue);
            }
        } else if (newValue === null) {
            setError(null);
            if (onValidationError) {
                onValidationError(false);
            }
            if (onChange) {
                onChange(null);
            }
        }
    };

    return (
        <MuiDatePicker
            {...restProps}
            // 8a285b3: `open={openProp}` idi. isPickerOpen (openProp ?? internalOpen)
            // hesaplaniyor ama SADECE getFieldSx'te kullaniliyordu; handleOpen'in
            // set ettigi internalOpen takvimi HIC acmiyordu.
            open={isPickerOpen}
            value={internalValue}
            inputFormat={inputFormat}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            shouldDisableYear={minDate || maxDate || userShouldDisableYear ? shouldDisableYear : undefined}
            shouldDisableMonth={minDate || maxDate || userShouldDisableMonth ? shouldDisableMonth : undefined}
            PopperProps={popperProps}
            PaperProps={paperProps}
            onOpen={handleOpen}
            onClose={handleClose}
            onAccept={handleAccept}
            onError={handleError}
            onChange={handleChange}
            renderInput={(params) => {
                const { inputProps, placeholder: _, inputRef, ...restParams } = params;

                return (
                    <Box ref={fieldWrapperRef} sx={{ position: "relative" }} id={id}>
                        <TextField
                            {...restParams}
                            inputRef={inputRef}
                            fullWidth={fullWidth}
                            size="small"
                            placeholder={placeholder}
                            error={!!error || !!externalError}
                            helperText=""
                            // Alanin kendisine tiklaninca da takvim acilsin; inputProps
                            // icindeki readOnly: true korunuyor -- kullanici elle
                            // yazmiyor, yalnizca tikliyor.
                            onClick={handleOpen}
                            inputProps={{
                                ...inputProps,
                                placeholder,
                                readOnly: true,
                            }}
                            sx={getFieldSx(isPickerOpen)}
                        />

                        {error && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "100%",
                                    left: "14px",
                                    marginTop: "4px",
                                    fontSize: "12px",
                                    fontFamily: "Inter",
                                    color: "#d32f2f",
                                    lineHeight: "20px",
                                    zIndex: 1,
                                }}
                            >
                                {error}
                            </Box>
                        )}
                    </Box>
                );
            }}
        />
    );
});

DatePicker.displayName = "DatePicker";

